"use client";
import { useState } from "react";
import fetchJson from "@/lib/fetchJson";
import { KeyedMutator } from "swr";
import {
  Button,
  Flex,
  IconButton,
  Text,
  TextField,
  Heading,
} from "@radix-ui/themes";
import { IconReload, IconUsers } from "@tabler/icons-react";
import * as Dialog from "@/components/CustomUiComponents/Dialog";

interface ScrapeCreatorsProps {
  campaignId: number;
  mutate: KeyedMutator<Campaign>;
  disabled?: boolean;
}

export default function ScrapeCreators({
  campaignId,
  mutate,
  disabled,
}: ScrapeCreatorsProps) {
  const [lookbackDays, setLookbackDays] = useState("5");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [isOpen, setIsOpen] = useState(false);

  const scrapeCreators = async () => {
    if (
      !lookbackDays ||
      isNaN(Number(lookbackDays)) ||
      Number(lookbackDays) < 1 ||
      Number(lookbackDays) > 30
    ) {
      alert(
        "Please enter a valid number of lookback days (minimum 1, maximum 30)"
      );
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      const response = await fetchJson(
        `/campaign/${campaignId}/scrape-creators?lookback_days=${lookbackDays}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      setResults(response);
      mutate(); // Refresh campaign data

      // Show success message
      alert(
        `Successfully scraped ${response.successfulScrapes}/${response.scrapedCreators} creators with ${response.totalPostsProcessed} posts processed`
      );
    } catch (error: any) {
      console.error("Error scraping creators:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // Reset state when modal closes
      setResults(null);
      setLookbackDays("7");
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Trigger>
        <IconButton
          variant="ghost"
          disabled={disabled || loading}
          title="Scrape All Creators"
        >
          <IconReload className="icon" />
          <IconUsers className="icon" />
        </IconButton>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Content style={{ maxWidth: "500px" }}>
          <Dialog.Title>Scrape All Campaign Creators</Dialog.Title>

          <Flex direction="column" gap="4" mt="4">
            <Text size="2" color="gray">
              This will scrape all creators associated with this campaign and
              process their recent posts. You can specify how many days back to
              look for new content. Please be mindful of the cost of scraping
              these creators.
            </Text>

            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Lookback Days
              </Text>
              <TextField.Root
                value={lookbackDays}
                onChange={(e) => setLookbackDays(e.target.value)}
                placeholder="7"
                type="number"
                min="1"
                max={"30"}
                variant="surface"
                className="force-black-bg"
              />
              <style jsx>{`
                :global(.force-black-bg input) {
                  background-color: var(--accent-a3) !important;
                  color: var(--accent-12) !important;
                }
              `}</style>
              <Text size="1" color="gray" mt="1">
                Number of days to look back for new posts (minimum 1)
              </Text>
            </label>

            {results && (
              <Flex
                direction="column"
                gap="2"
                p="3"
                style={{
                  backgroundColor: "var(--gray-2)",
                  borderRadius: "8px",
                }}
              >
                <Heading size="3">Scrape Results</Heading>
                <Text size="2">Campaign: {results.campaignName}</Text>
                <Text size="2">Total Creators: {results.totalCreators}</Text>
                <Text size="2">Scraped: {results.scrapedCreators}</Text>
                <Text size="2">Successful: {results.successfulScrapes}</Text>
                <Text size="2">
                  Posts Processed: {results.totalPostsProcessed}
                </Text>
                <Text size="2">
                  Lookback Period: {results.lookbackDays} days
                </Text>
              </Flex>
            )}
          </Flex>

          <Flex direction="row" gap="3" mt="6" justify="end">
            <Dialog.Close>
              <Button variant="outline" disabled={loading}>
                {results ? "Close" : "Cancel"}
              </Button>
            </Dialog.Close>
            {!results && (
              <Button onClick={scrapeCreators} disabled={loading}>
                {loading ? "Scraping..." : "Start Scraping"}
              </Button>
            )}
          </Flex>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
